import { useState, useEffect, useRef, useCallback } from 'react';
import Main from '../../components/layouts/Main';
import PageTitle from '../../components/utils/PageTitle';
import Breadcrumb from '../../components/utils/Breadcrumb';
import { useUserContext } from '../../contexts/UserContext';
import { Button, Card, Collapse, Spin, Empty } from 'antd';
import {
  BellOutlined, PhoneOutlined, CheckSquareOutlined,
  CalendarOutlined, ReloadOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import AdvancedFilterPanel from '../../components/utils/AdvancedFilterPanel';
import ActivityItem from '../../components/Activity/ActivityItem';
import ActiviteDoneSection from '../../components/activite/ActiviteDoneSection';
import ActiviteService from '../../services/ActiviteService';
import UserService from '../../services/UserService';
import { catchError } from '../../services/DaoService';
import styles from './ActiviteListPage.module.scss';

const TYPE_OPTIONS = [
  { value: null,       label: 'Tous les types' },
  { value: 'Appel',   label: 'Appel',   icon: <PhoneOutlined />,       color: '#FA8C16' },
  { value: 'Tache',   label: 'Tâche',   icon: <CheckSquareOutlined />, color: 'var(--primary-color, #3E65AC)' },
  { value: 'Rappel',  label: 'Rappel',  icon: <BellOutlined />,        color: '#722ED1' },
  { value: 'Reunion', label: 'Réunion', icon: <CalendarOutlined />,    color: '#52C41A' },
];

const ROLE_OPTIONS = [
  { value: null,              label: 'Toutes' },
  { value: 'assigned_by_me', label: "Que j'ai assignées" },
  { value: 'assigned_to_me', label: "Qui me sont assignées" },
];

export default function MesActivitesListPage() {
  const { check } = useUserContext();
  const [filters, setFilters] = useState({ searchText: null, type: null, my_role: null });
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const sentinelRef = useRef(null);

  useEffect(() => {
    UserService.getInstance().all()
      .then(r => setUsers(r.data))
      .catch(catchError);
  }, []);

  const load = useCallback(async (currentFilters) => {
    setLoading(true);
    try {
      const res = await ActiviteService.getInstance().search({ ...currentFilters, statut: 'pending' });
      setList(res.data.data);
      setPagination(res.data);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchText, filters.type, filters.my_role]);

  const loadMore = useCallback(async () => {
    if (!pagination?.next_page_url || loading) return;
    setLoading(true);
    try {
      const res = await ActiviteService.post(pagination.next_page_url, { ...filters, statut: 'pending' });
      setList(prev => [...prev, ...res.data.data]);
      setPagination(res.data);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, [pagination, loading, filters]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && pagination?.next_page_url && !loading) loadMore();
  }, [pagination, loading, loadMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const handleToggleDone = (updated) => {
    if (updated.done) {
      setList(prev => prev.filter(a => a.id !== updated.id));
    } else {
      setList(prev => [updated, ...prev.filter(a => a.id !== updated.id)]);
    }
  };

  const handleDelete = (activity) => {
    setList(prev => prev.filter(a => a.id !== activity.id));
  };

  const handleUpdate = (updated) => {
    setList(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  return (
    <Main>
      <Breadcrumb parentLink="/" canSee={check('VIEW-DASHBOARD')} parentLinkTitle="Accueil" title="Mes activités" />
      <PageTitle
        title="Mes activités"
        subTitle={`${list.length} activité${list.length !== 1 ? 's' : ''}${pagination?.total ? ` sur ${pagination.total}` : ''} en cours`}
      >
        <Button icon={<ReloadOutlined />} onClick={() => load(filters)}>Rafraîchir</Button>
      </PageTitle>

      <Card styles={{ body: { padding: 0 } }}>
        <AdvancedFilterPanel
          filters={filters}
          onFilterChange={setFilter}
          searchPlaceholder="Rechercher une activité..."
          searchValue={filters.searchText || ''}
          onSearch={v => setFilter('searchText', v || null)}
          showRoleFilter={true}
          roleFilterValue={filters.my_role}
          onRoleFilterChange={v => setFilter('my_role', v)}
          roleOptions={ROLE_OPTIONS}
        />

        {/* Filtre par type */}
        <div className={styles.typeBarWrapper}>
          <div className={styles.typeBar}>
            {TYPE_OPTIONS.map(opt => {
              const active = filters.type === opt.value;
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  className={`${styles.typeChip} ${active ? styles.typeChipActive : ''}`}
                  style={
                    active && opt.color
                      ? { background: opt.color, borderColor: opt.color }
                      : opt.color
                        ? { borderColor: opt.color, color: opt.color }
                        : {}
                  }
                  onClick={() => setFilter('type', opt.value)}
                >
                  {opt.icon && <span className={styles.typeChipIcon}>{opt.icon}</span>}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Section En cours */}
        <Collapse
          defaultActiveKey={['active']}
          ghost={false}
          style={{ borderRadius: 0 }}
          items={[{
            key: 'active',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
                <ThunderboltOutlined style={{ color: 'var(--primary-color, #3E65AC)' }} />
                En cours
                {pagination?.total != null && (
                  <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({pagination.total})</span>
                )}
              </span>
            ),
            children: (
              <Spin spinning={loading && list.length === 0}>
                {list.length === 0 && !loading
                  ? <div style={{ padding: '24px 0' }}><Empty description="Aucune activité en cours" /></div>
                  : (
                    <div>
                      {list.map(a => (
                        <div key={a.id} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                          <ActivityItem
                            item={a}
                            users={users}
                            onDelete={handleDelete}
                            onToggleDone={handleToggleDone}
                            onUpdate={handleUpdate}
                          />
                        </div>
                      ))}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loading && list.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                          <Spin size="small" />
                        </div>
                      )}
                    </div>
                  )
                }
              </Spin>
            ),
          }]}
        />

        {/* Section Terminées — chargement lazy */}
        <ActiviteDoneSection
          filters={filters}
          users={users}
          onToggleDone={handleToggleDone}
          onDelete={handleDelete}
        />
      </Card>
    </Main>
  );
}
